import { DateTimePicker } from '@mantine/dates'
import {
  Card,
  Checkbox,
  createTheme,
  Modal,
  RadioCard,
  ScrollArea,
  SegmentedControl,
  Switch,
  type CSSVariablesResolver,
  type MantineColorsTuple,
} from '@mantine/core'
import { ColorPrimitives, SizePrimitives, Typography } from '@front/model'
import { themeRadius } from './util'
import switchClasses from './Switch.module.css'

const getMantineColorTupleFromPrimitive = (key: keyof typeof ColorPrimitives): MantineColorsTuple => {
  return Object.values(ColorPrimitives[key]) as unknown as MantineColorsTuple
}

export const themeCssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: { '--mantine-color-dimmed': '#5a6067' },
  dark: { '--mantine-color-dimmed': '#92a1b3' },
})

export const theme = createTheme({
  colors: {
    dark: [
      '#b5c4d6',
      '#8c9eb5',
      '#6b7f98',
      '#4d627a',
      '#33475e',
      '#203144',
      '#152231',
      '#0d1622',
      '#080f17',
      '#05090f',
      '#09131d',
    ],
    blue: [
      '#a2acb4',
      '#8b97a2',
      '#74828f',
      '#5d6d7c',
      '#455969',
      '#2e4457',
      '#172F44',
      '#152a3d',
      '#122636',
      '#102130',
    ],
    brand: getMantineColorTupleFromPrimitive('blue'),
    yellow: getMantineColorTupleFromPrimitive('yellow'),
    tan: getMantineColorTupleFromPrimitive('tan'),
    neutral: getMantineColorTupleFromPrimitive('neutral'),
    rose: getMantineColorTupleFromPrimitive('rose'),
    orange: getMantineColorTupleFromPrimitive('orange'),
    emerald: getMantineColorTupleFromPrimitive('emerald'),
    indigo: getMantineColorTupleFromPrimitive('indigo'),
    black: getMantineColorTupleFromPrimitive('black'),
    white: getMantineColorTupleFromPrimitive('white'),
  },
  fontFamily: 'Sora, sans-serif',
  fontSizes: Object.entries(Typography.size).reduce<Record<string, string>>((acc, [key, value]) => {
    acc[key] = `${value}px`
    return acc
  }, {}),
  lineHeights: Object.entries(Typography.line).reduce<Record<string, string>>((acc, [key, value]) => {
    acc[key] = `${value}px`
    return acc
  }, {}),
  primaryColor: 'brand',
  primaryShade: { light: 6, dark: 7 },
  defaultRadius: 'xl',
  radius: themeRadius,
  components: {
    Modal: Modal.extend({
      defaultProps: {
        radius: '2xl',
      },
    }),
    Card: Card.extend({
      defaultProps: {
        radius: '2xl',
        padding: 'md',
        shadow: 'md',
      },
    }),
    RadioCard: RadioCard.extend({
      defaultProps: {
        radius: '2xl',
      },
    }),
    Switch: Switch.extend({
      defaultProps: {
        withThumbIndicator: false,
        classNames: switchClasses,
      },
    }),
    Checkbox: Checkbox.extend({
      defaultProps: {
        radius: 'md',
      },
    }),
    DateTimePicker: DateTimePicker.extend({
      defaultProps: {
        timePickerProps: { format: '12h' },
      },
    }),
    SegmentedControl: SegmentedControl.extend({
      defaultProps: {
        color: 'brand',
      },
    }),
    ScrollArea: ScrollArea.extend({
      defaultProps: {
        scrollbarSize: SizePrimitives['1.5'],
      },
    }),
  },
})
